// React & External Libraries
import { useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { saveAs } from "file-saver";

// Internal Components & Types
import Button from "../main/Button";
import Icon from "../main/Icon";
import { IEmployee } from "../../interfaces";

// Styles
import "../styles/Actions.css";

// Types for better type safety
type ContactField = {
  type: string;
  value: string;
};

type ContactProcessor = (value: string) => string;

// vCard field generators - extracted for better organization
const vCardFields = {
  version: () => `\nVERSION:3.0`,
  name: (employee: IEmployee) => `\nN:${employee.lastName};${employee.firstName};`,
  formatedName: (name: string) => `\nFN:${name}`,
  organization: (org: string) => `\nORG:${org}`,
  title: (title: string) => `\nTITLE:${title}`,
  workPhone: (phone: string) => `\nTEL;type=WORK;VOICE:${phone}`,
  cellPhone: (phone: string) => `\nTEL;type=CELL;MOBILE:${phone}`,
  address: (address: string) => `\nADR;type=WORK;type=pref:;;${address}`,
  geolocation: (lat: string, lng: string) => `\nGEO:geo:${lat}\\,${lng}`,
  photo: (photo: string) => `\nPHOTO;TYPE=JPEG;ENCODING=BASE64:${photo}`,
  email: (email: string) => `\nEMAIL:${email}`,
  timestamp: () => `\nREV:${new Date().toISOString()}`,
  url: (url: string) => `\nURL:${url}`,
  socialProfile: (type: string, value: string) => `\nX-SOCIALPROFILE;TYPE=${type}:${value}`,
  socialProfileNoLink: (type: string, value: string) => `\nX-SOCIALPROFILE;TYPE=${type};VALUE=TEXT:${value}`
};

// Contact type mapping - easily extensible for new platforms
const contactTypeMapping: Record<string, ContactProcessor> = {
  // Communication
  email: vCardFields.email,
  phone: vCardFields.workPhone,
  mobile: vCardFields.cellPhone,
  website: vCardFields.url,
  
  // Social Media - easy to extend
  whatsapp: (value) => vCardFields.socialProfileNoLink('whatsapp', value),
  facebook: (value) => vCardFields.socialProfileNoLink('facebook', value),
  instagram: (value) => vCardFields.socialProfile('instagram', value),
  linkedin: (value) => vCardFields.socialProfile('linkedin', value),
  twitter: (value) => vCardFields.socialProfile('twitter', value),
  tiktok: (value) => vCardFields.socialProfile('tiktok', value),
  youtube: (value) => vCardFields.socialProfile('youtube', value),
  googleBusinessMessage: (value) => vCardFields.socialProfileNoLink('googlebusiness', value),
  
  // Messaging Apps - no clickable links for privacy/security
  telegram: (value) => vCardFields.socialProfileNoLink('telegram', value),
  signal: (value) => vCardFields.socialProfileNoLink('signal', value),
  discord: (value) => vCardFields.socialProfileNoLink('discord', value),
  
  // Professional Networks
  xing: (value) => vCardFields.socialProfile('xing', value),
  github: (value) => vCardFields.socialProfile('github', value),
};

// Utility function to add new contact types at runtime
const addContactType = (type: string, processor: ContactProcessor): void => {
  contactTypeMapping[type] = processor;
};

// Helper to add social media platforms easily
const addSocialMediaType = (type: string, socialType?: string): void => {
  const platformName = socialType || type;
  addContactType(type, (value) => vCardFields.socialProfile(platformName, value));
};

// Example usage for extending contact types:
// addSocialMediaType('snapchat');
// addSocialMediaType('threads'); 
// addContactType('skype', (value) => vCardFields.socialProfile('skype', value));
// addContactType('customField', (value) => `\nX-CUSTOM:${value}`);

function Actions({ employee }: { employee: IEmployee }) {
  const isMobile = useMediaQuery({ query: "(width <= 700px)" });

  // Extract contact fields logic
  const getContactFields = (employee: IEmployee): ContactField[] => {
    return [
      { type: 'email', value: employee.email },
      { type: 'phone', value: employee.phone },
      { type: 'mobile', value: employee.mobile },
      { type: 'whatsapp', value: employee.whatsapp },
      { type: 'website', value: employee.website },
      { type: 'facebook', value: employee.facebook },
      { type: 'googleBusinessMessage', value: employee.googleBusinessMessage }
    ].filter(contact => contact.value?.trim());
  };

  // Build address string helper
  const buildAddressString = (address: any): string => {
    return `${address.street} ${address.houseNumber};${address.city};;${address.zip}`;
  };

  // Process dynamic contacts - fully flexible and extensible
  const processContactFields = (contactFields: ContactField[]): string => {
    return contactFields
      .map(({ type, value }) => {
        const processor = contactTypeMapping[type];
        return processor ? processor(value) : '';
      })
      .join('');
  };

  // Main vCard generation with better structure
  const vCardData = useMemo(() => {
    const { teams, brandKey } = employee;
    const addressString = buildAddressString(teams.address);
    const contactFields = getContactFields(employee);
    
    return "BEGIN:VCARD" +
      vCardFields.version() +
      vCardFields.name(employee) +
      vCardFields.formatedName(`${employee.firstName} ${employee.lastName}`) +
      vCardFields.organization(brandKey.brandName) +
      vCardFields.title(employee.role) +
      vCardFields.address(addressString) +
      vCardFields.geolocation(teams.address.latitude, teams.address.longtitude) +
      vCardFields.photo(employee.base64ProfileImage) +
      processContactFields(contactFields) +
      vCardFields.timestamp() +
      "\nEND:VCARD";
  }, [employee]);

  const handleDownload = (): void => {
    const blob = new Blob([vCardData], { type: "text/vcard" });
    saveAs(blob, `${employee.urlKey}.vcf`);
  };

  return (
    <div className="container" style={{ gap: `${isMobile ? 10 : 15}px` }}>
      <Button
        buttonText="Save contact"
        rigthIcon={
          <Icon
            icon="download"
            color="white"
            size="19px"
          />
        }
        onClick={handleDownload}
        size="large"
      />
      <div id="od_widget_appointment" />
    </div>
  );
}

export default Actions;
